import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Trophy, 
  Users, 
  Calendar, 
  MapPin, 
  Loader2,
  Plus,
  Swords,
  Clock,
  Info,
  User,
  Award
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ROUND_TYPES = [
  { value: 'League', label: 'League' },
  { value: 'Quarter Final', label: 'Quarter Final' },
  { value: 'Semi Final', label: 'Semi Final' },
  { value: 'Final', label: 'Final' },
];

export default function EventViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  
  // Teams state
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  
  // Participants state (users who joined)
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  
  // Matches state
  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  
  // Create Match Modal
  const [showCreateMatchModal, setShowCreateMatchModal] = useState(false);
  const [creatingMatch, setCreatingMatch] = useState(false);
  const [matchForm, setMatchForm] = useState({
    team1_id: '',
    team2_id: '',
    round: 'League',
    date: '',
    time: '',
    location: '',
    court: '',
  });

  // Fetch event details
  const fetchEvent = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/events/${id}`);
      setEvent(response.data);
      
      // Also fetch fixtures/matches from the event
      if (response.data.fixtures) {
        setMatches(response.data.fixtures);
      }
    } catch (error) {
      toast.error('Failed to load event details');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // Fetch registered teams for this event
  const fetchTeams = useCallback(async () => {
    if (!event) return;
    
    setLoadingTeams(true);
    try {
      // Get team IDs from event
      const teamIds = event.team_ids || event.registered_teams || [];
      
      if (teamIds.length === 0) {
        setTeams([]);
        return;
      }
      
      // Fetch teams in bulk
      const response = await axios.get(`${API_URL}/api/teams/bulk?ids=${teamIds.join(',')}`);
      setTeams(response.data.teams || response.data || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      // Fallback: try to get teams from event data
      if (event.teams && Array.isArray(event.teams)) {
        setTeams(event.teams);
      } else {
        setTeams([]);
      }
    } finally {
      setLoadingTeams(false);
    }
  }, [event]);

  // Fetch participants (users who joined) for this event
  const fetchParticipants = useCallback(async () => {
    if (!event) return;
    
    setLoadingParticipants(true);
    try {
      // Get participant IDs from event
      const participantIds = event.participants || [];
      
      if (participantIds.length === 0) {
        setParticipants([]);
        return;
      }
      
      // Fetch users in bulk
      const response = await axios.get(`${API_URL}/api/users/bulk?ids=${participantIds.join(',')}`);
      setParticipants(response.data.users || response.data || []);
    } catch (error) {
      console.error('Failed to fetch participants:', error);
      setParticipants([]);
    } finally {
      setLoadingParticipants(false);
    }
  }, [event]);

  // Fetch matches for this event
  const fetchMatches = useCallback(async () => {
    if (!event) return;
    
    setLoadingMatches(true);
    try {
      const response = await axios.get(`${API_URL}/api/events/${id}/matches`);
      setMatches(response.data.matches || response.data || []);
    } catch (error) {
      // Use fixtures from event if available
      if (event.fixtures) {
        setMatches(event.fixtures);
      }
    } finally {
      setLoadingMatches(false);
    }
  }, [event, id]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  useEffect(() => {
    if (event && activeTab === 'teams') {
      fetchTeams();
      fetchParticipants();
    }
    if (event && activeTab === 'matches') {
      fetchMatches();
      // Also fetch participants/teams for match creation
      fetchTeams();
      fetchParticipants();
    }
  }, [event, activeTab, fetchTeams, fetchParticipants, fetchMatches]);

  // Handle create match
  const handleCreateMatch = async () => {
    if (!matchForm.team1_id || !matchForm.team2_id) {
      toast.error('Please select both participants');
      return;
    }
    
    if (matchForm.team1_id === matchForm.team2_id) {
      toast.error('Participants must be different');
      return;
    }
    
    setCreatingMatch(true);
    try {
      // Get participant/team names from available options
      const matchParticipants = getMatchParticipants();
      const participant1 = matchParticipants.find(p => p.id === matchForm.team1_id);
      const participant2 = matchParticipants.find(p => p.id === matchForm.team2_id);
      
      // Get existing fixtures
      const existingFixtures = event.fixtures || [];
      
      // Create new fixture object
      const newFixture = {
        match_number: existingFixtures.length + 1,
        team1_id: matchForm.team1_id,
        team1_name: participant1?.name || 'Player 1',
        team2_id: matchForm.team2_id,
        team2_name: participant2?.name || 'Player 2',
        round: matchForm.round || 'League',
        date: matchForm.date || null,
        time: matchForm.time || null,
        location: matchForm.location || null,
        court: matchForm.court || null,
        status: 'scheduled'
      };
      
      // Save fixtures with confirmed status to create match document
      const response = await axios.patch(`${API_URL}/api/events/${id}/fixtures`, {
        fixtures: [...existingFixtures, newFixture],
        status: 'confirmed'
      });
      
      toast.success('Match created successfully!');
      setShowCreateMatchModal(false);
      
      // Reset form
      setMatchForm({
        team1_id: '',
        team2_id: '',
        round: 'League',
        date: '',
        time: '',
        location: '',
        court: '',
      });
      
      // Refresh event data
      fetchEvent();
      fetchMatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create match');
    } finally {
      setCreatingMatch(false);
    }
  };

  // Check if can create matches - works with both teams and participants
  const canCreateMatch = () => {
    if (!event) return false;
    if (event.status === 'completed' || event.status === 'cancelled') return false;
    
    // Check for teams first
    const teamCount = teams.length || event.team_ids?.length || event.registered_teams?.length || 0;
    if (teamCount >= 2) return true;
    
    // If no teams, check for participants
    const participantCount = participants.length || event.participants?.length || 0;
    return participantCount >= 2;
  };

  // Get available match participants (teams or individual participants)
  const getMatchParticipants = () => {
    // If we have teams, use them
    if (teams.length >= 2) {
      return teams.map(t => ({ id: t.id, name: t.name, type: 'team' }));
    }
    // Otherwise use individual participants
    return participants.map(p => ({ id: p.id, name: p.name || 'Unknown', type: 'participant' }));
  };

  // Get sport icon
  const getSportIcon = (sport) => {
    const sportIcons = {
      'table_tennis': '🏓',
      'badminton': '🏸',
      'tennis': '🎾',
      'cricket': '🏏',
      'volleyball': '🏐',
      'basketball': '🏀',
    };
    return sportIcons[sport] || '🏆';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Event not found</p>
        <Button onClick={() => navigate('/events')} variant="outline" className="mt-4">
          Back to Events
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/events')}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center text-2xl">
              {event.banner_image ? (
                <img src={event.banner_image} alt="" className="w-12 h-12 rounded-lg object-cover" />
              ) : (
                getSportIcon(event.sport)
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{event.name || 'Unnamed Event'}</h1>
              <p className="text-slate-400">{event.sport} • {event.format_type || event.tournament_type}</p>
            </div>
          </div>
        </div>
        
        <Button
          onClick={() => navigate(`/events/${id}?mode=edit`)}
          variant="outline"
          className="border-slate-700 text-slate-300"
        >
          Edit Event
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="details" className="data-[state=active]:bg-green-500 data-[state=active]:text-slate-900 gap-2">
            <Info className="w-4 h-4" />
            Details
          </TabsTrigger>
          <TabsTrigger value="teams" className="data-[state=active]:bg-green-500 data-[state=active]:text-slate-900 gap-2">
            <Users className="w-4 h-4" />
            Participants
            <span className="ml-1 px-2 py-0.5 rounded-full bg-slate-700 text-xs">
              {event.participants?.length || event.team_ids?.length || 0}
            </span>
          </TabsTrigger>
          <TabsTrigger value="matches" className="data-[state=active]:bg-green-500 data-[state=active]:text-slate-900 gap-2">
            <Swords className="w-4 h-4" />
            Matches
            <span className="ml-1 px-2 py-0.5 rounded-full bg-slate-700 text-xs">
              {event.fixtures?.length || matches.length || 0}
            </span>
          </TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Event Info Card */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-purple-400" />
                  Event Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-400">Sport</p>
                    <p className="text-white">{event.sport || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Format</p>
                    <p className="text-white capitalize">{event.format_type || event.sport_format || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Tournament Type</p>
                    <p className="text-white capitalize">{event.tournament_type || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Scope</p>
                    <p className="text-white capitalize">{event.scope || 'Open'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Description</p>
                  <p className="text-white">{event.description || 'No description'}</p>
                </div>
              </CardContent>
            </Card>

            {/* Schedule Card */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-400">Event Start</p>
                    <p className="text-white">{event.event_start ? formatDateTime(event.event_start) : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Event End</p>
                    <p className="text-white">{event.event_end ? formatDateTime(event.event_end) : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Registration Start</p>
                    <p className="text-white">{event.registration_start ? formatDateTime(event.registration_start) : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Registration End</p>
                    <p className="text-white">{event.registration_end ? formatDateTime(event.registration_end) : '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Venue Card */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-red-400" />
                  Venue
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-white font-medium">{event.venue_name || 'Not specified'}</p>
                <p className="text-slate-400">
                  {[event.city, event.state, event.country].filter(Boolean).join(', ') || 'Location not set'}
                </p>
              </CardContent>
            </Card>

            {/* Entry Fee Card */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-400" />
                  Entry & Prizes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-400">Entry Fee</p>
                  <p className="text-2xl font-bold text-green-400">
                    {event.is_free ? 'Free' : `₹${event.entry_fee || 0}`}
                  </p>
                </div>
                {event.prizes && event.prizes.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-400 mb-2">Prizes</p>
                    {event.prizes.filter(p => p.prize).map((prize, idx) => (
                      <p key={idx} className="text-white">
                        {prize.title}: <span className="text-yellow-400">{prize.prize}</span>
                      </p>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Teams/Participants Tab */}
        <TabsContent value="teams" className="mt-6">
          {(loadingTeams || loadingParticipants) ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-500" />
            </div>
          ) : (participants.length === 0 && teams.length === 0) ? (
            <div className="text-center py-12 bg-slate-900/50 rounded-lg border border-slate-800">
              <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No participants registered yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Participants Section */}
              {participants.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-green-400" />
                    Participants ({participants.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {participants.map((participant, idx) => (
                      <Card key={participant.id || idx} className="bg-slate-900 border-slate-800">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center overflow-hidden">
                              {(participant.avatar_url || participant.profile_image) ? (
                                <img 
                                  src={participant.avatar_url || participant.profile_image} 
                                  alt={participant.name} 
                                  className="w-12 h-12 rounded-full object-cover" 
                                />
                              ) : (
                                <User className="w-6 h-6 text-green-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{participant.name || 'Unknown User'}</p>
                              <p className="text-sm text-slate-400">{participant.email || participant.phone || ''}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Teams Section */}
              {teams.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-400" />
                    Teams ({teams.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teams.map((team, idx) => (
                      <Card key={team.id || idx} className="bg-slate-900 border-slate-800">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-indigo-500/20 flex items-center justify-center">
                              {team.logo_url ? (
                                <img src={team.logo_url} alt={team.name} className="w-12 h-12 rounded-full object-cover" />
                              ) : (
                                <Users className="w-6 h-6 text-indigo-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{team.name}</p>
                              <p className="text-sm text-slate-400">{team.member_count || team.members?.length || 0} members</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Matches Tab */}
        <TabsContent value="matches" className="mt-6">
          {/* Create Match Button */}
          {canCreateMatch() && (
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => setShowCreateMatchModal(true)}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Match
              </Button>
            </div>
          )}

          {loadingMatches ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-500" />
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12 bg-slate-900/50 rounded-lg border border-slate-800">
              <Swords className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 mb-2">No matches scheduled yet</p>
              {canCreateMatch() && (
                <Button
                  onClick={() => setShowCreateMatchModal(true)}
                  variant="outline"
                  className="border-green-500 text-green-400 hover:bg-green-500/10"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Match
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {matches.map((match, idx) => (
                <Card key={match.id || idx} className="bg-slate-900 border-slate-800">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Match #{match.match_number || idx + 1}</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            match.round === 'Final' ? 'bg-yellow-500/20 text-yellow-400' :
                            match.round === 'Semi Final' ? 'bg-purple-500/20 text-purple-400' :
                            match.round === 'Quarter Final' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-slate-700 text-slate-300'
                          }`}>
                            {match.round || 'League'}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {/* Team 1 */}
                          <div className="text-right min-w-[120px]">
                            <p className="text-white font-medium">{match.team1_name || 'Team 1'}</p>
                            {match.score_a !== undefined && (
                              <p className="text-2xl font-bold text-green-400">{match.score_a}</p>
                            )}
                          </div>
                          
                          <span className="text-slate-500 text-lg font-bold">VS</span>
                          
                          {/* Team 2 */}
                          <div className="text-left min-w-[120px]">
                            <p className="text-white font-medium">{match.team2_name || 'Team 2'}</p>
                            {match.score_b !== undefined && (
                              <p className="text-2xl font-bold text-green-400">{match.score_b}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          match.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          match.status === 'in_progress' || match.status === 'live' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-slate-700 text-slate-300'
                        }`}>
                          {match.status || 'scheduled'}
                        </span>
                        {(match.date || match.time) && (
                          <p className="text-xs text-slate-500 mt-1">
                            {match.date && formatDate(match.date)} {match.time}
                          </p>
                        )}
                        {match.location && (
                          <p className="text-xs text-slate-500">{match.location} {match.court && `• ${match.court}`}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Match Modal */}
      <Dialog open={showCreateMatchModal} onOpenChange={setShowCreateMatchModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Swords className="w-5 h-5 text-green-400" />
              Create Match
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Player/Team 1 Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300">{teams.length >= 2 ? 'Team 1' : 'Player 1'} *</Label>
              <Select 
                value={matchForm.team1_id} 
                onValueChange={(v) => setMatchForm(prev => ({ ...prev, team1_id: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder={teams.length >= 2 ? 'Select Team 1' : 'Select Player 1'} />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {getMatchParticipants().filter(p => p.id !== matchForm.team2_id).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Player/Team 2 Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300">{teams.length >= 2 ? 'Team 2' : 'Player 2'} *</Label>
              <Select 
                value={matchForm.team2_id} 
                onValueChange={(v) => setMatchForm(prev => ({ ...prev, team2_id: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue placeholder={teams.length >= 2 ? 'Select Team 2' : 'Select Player 2'} />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {getMatchParticipants().filter(p => p.id !== matchForm.team1_id).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Round Selection */}
            <div className="space-y-2">
              <Label className="text-slate-300">Round</Label>
              <Select 
                value={matchForm.round} 
                onValueChange={(v) => setMatchForm(prev => ({ ...prev, round: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {ROUND_TYPES.map(round => (
                    <SelectItem key={round.value} value={round.value}>{round.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Optional Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Date</Label>
                <Input
                  type="date"
                  value={matchForm.date}
                  onChange={(e) => setMatchForm(prev => ({ ...prev, date: e.target.value }))}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Time</Label>
                <Input
                  type="text"
                  value={matchForm.time}
                  onChange={(e) => setMatchForm(prev => ({ ...prev, time: e.target.value }))}
                  placeholder="e.g., 10:00 AM"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Location</Label>
              <Input
                value={matchForm.location}
                onChange={(e) => setMatchForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Main Ground"
                className="bg-slate-800 border-slate-700"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Court/Field</Label>
              <Input
                value={matchForm.court}
                onChange={(e) => setMatchForm(prev => ({ ...prev, court: e.target.value }))}
                placeholder="e.g., Court 1"
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateMatchModal(false)}
              className="border-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateMatch}
              disabled={creatingMatch || !matchForm.team1_id || !matchForm.team2_id}
              className="bg-green-500 hover:bg-green-600"
            >
              {creatingMatch ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Match'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
